import {
  defaultColumnHeaders,
  defaultRowHeaders,
  getTableDimensions,
  parseTableCells,
  resizeHeaders
} from '../editForm/tableFieldUtils'
import {
  extractConditionalEditColumnsFromForm,
  getConditionalEditOriginalDisplay,
  getPrefillEditForTableCell,
  type PrefillFieldEdit
} from './submissionPrefillEditUtils'

const NON_INPUT_FIELD_TYPES = new Set([
  'heading',
  'subheading',
  'banner',
  'formLogo',
  'divider',
  'button',
  'prevPageButton',
  'nextPageButton',
  'captcha',
  'signature',
  'addPage',
  'buttonRow'
])

export interface TableFieldMeta {
  fieldId: number
  label: string
  rows: number
  cols: number
  rowHeaders: string[]
  columnHeaders: string[]
}

export interface FlatSubmissionColumn {
  key: string
  label: string
  fieldId: number
  isTableCell: boolean
  tableLabel?: string
  rowIndex?: number
  colIndex?: number
  isConditionalEdit?: boolean
  parentFieldId?: number
  editFieldType?: string
}

export interface FormValueEntry {
  label?: string
  value?: unknown
  fieldId?: number
}

export const normalizeFormData = (formData: unknown): unknown => {
  let normalized = formData
  for (let index = 0; index < 2; index += 1) {
    if (typeof normalized !== 'string') break
    try {
      normalized = JSON.parse(normalized)
    } catch {
      return null
    }
  }
  return normalized
}

export const normalizeFormValueEntries = (formValue: unknown): FormValueEntry[] => {
  if (!formValue) return []
  let entries = formValue
  if (typeof formValue === 'string') {
    try {
      entries = JSON.parse(formValue)
    } catch {
      return []
    }
  }
  if (!Array.isArray(entries)) return []
  return entries as FormValueEntry[]
}

const flattenFormPages = (formData: unknown): any[] => {
  const normalized = normalizeFormData(formData)
  if (!normalized) return []
  let pages = Array.isArray(normalized) ? normalized : []
  if (pages.length > 0 && !Array.isArray(pages[0])) {
    pages = [pages]
  }
  return pages.flat().filter(Boolean)
}

export const extractTableFieldsFromForm = (formData: unknown): Map<number, TableFieldMeta> => {
  const tableFields = new Map<number, TableFieldMeta>()

  flattenFormPages(formData).forEach((element) => {
    if (element?.type !== 'table') return
    const fieldId = element.fieldId ?? element.id
    if (fieldId === undefined || fieldId === null) return
    const label = String(element.label || 'Table').trim()
    const { rows, cols } = getTableDimensions(element)
    tableFields.set(Number(fieldId), {
      fieldId: Number(fieldId),
      label,
      rows,
      cols,
      rowHeaders: resizeHeaders(element.rowHeaders, rows, defaultRowHeaders),
      columnHeaders: resizeHeaders(element.columnHeaders, cols, defaultColumnHeaders)
    })
  })

  return tableFields
}

const parseTableMatrix = (value: unknown): string[][] | null => {
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    if (!Array.isArray(value[0])) {
      return [value.map((cell) => String(cell ?? ''))]
    }
    return value.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []))
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value)
      if (!Array.isArray(parsed) || parsed.length === 0) return null
      if (!Array.isArray(parsed[0])) {
        return [parsed.map((cell) => String(cell ?? ''))]
      }
      return parsed.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []))
    } catch {
      return null
    }
  }
  return null
}

const extractTableCellValue = (
  rawValue: unknown,
  rowIndex: number,
  colIndex: number,
  meta: TableFieldMeta
): string => {
  const matrix = parseTableMatrix(rawValue)
  if (matrix) {
    const cells = parseTableCells(rawValue, meta.rows, meta.cols)
    return cells[rowIndex]?.[colIndex] ?? ''
  }

  // Legacy submissions stored table arrays via String(value) -> "a,b,c,d"
  if (rowIndex === 0 && typeof rawValue === 'string' && rawValue.includes(',')) {
    const parts = rawValue.split(',').map((part) => part.trim())
    if (parts.length === meta.cols) {
      return parts[colIndex] ?? ''
    }
  }

  return ''
}

const inferTableMetaFromValue = (
  fieldId: number,
  label: string,
  value: unknown
): TableFieldMeta | null => {
  const matrix = parseTableMatrix(value)
  if (!matrix) return null
  const rows = matrix.length
  const cols = Math.max(...matrix.map((row) => row.length), 1)
  return {
    fieldId,
    label,
    rows,
    cols,
    rowHeaders: defaultRowHeaders(rows),
    columnHeaders: defaultColumnHeaders(cols)
  }
}

const mergeTableFieldMeta = (
  base: TableFieldMeta | undefined,
  value: unknown,
  fieldId: number,
  label: string
): TableFieldMeta | null => {
  const fromValue = inferTableMetaFromValue(fieldId, label, value)
  if (!base && !fromValue) return null
  if (!base) return fromValue
  if (!fromValue) return base

  const rows = Math.max(base.rows, fromValue.rows)
  const cols = Math.max(base.cols, fromValue.cols)

  return {
    fieldId,
    label: base.label || label,
    rows,
    cols,
    rowHeaders: resizeHeaders(base.rowHeaders, rows, defaultRowHeaders),
    columnHeaders: resizeHeaders(base.columnHeaders, cols, defaultColumnHeaders)
  }
}

const countPopulatedTableRows = (value: unknown): number => {
  const matrix = parseTableMatrix(value)
  if (!matrix) return 0

  let lastPopulatedIndex = -1
  matrix.forEach((row, rowIndex) => {
    if (row.some((cell) => String(cell ?? '').trim())) {
      lastPopulatedIndex = rowIndex
    }
  })

  return lastPopulatedIndex + 1
}

const applyDataDrivenTableRows = (
  meta: TableFieldMeta,
  formValueEntries: FormValueEntry[]
): TableFieldMeta => {
  const matchingValues = formValueEntries.filter((entry) => Number(entry.fieldId) === meta.fieldId)
  const populatedRows = matchingValues.reduce(
    (max, entry) => Math.max(max, countPopulatedTableRows(entry.value)),
    0
  )

  const effectiveRows =
    populatedRows > 0 ? populatedRows : matchingValues.length > 0 ? 0 : meta.rows

  if (effectiveRows === meta.rows) return meta

  return {
    ...meta,
    rows: effectiveRows,
    rowHeaders: resizeHeaders(meta.rowHeaders, effectiveRows, defaultRowHeaders)
  }
}

const buildTableCellColumns = (
  meta: TableFieldMeta,
  conditionalColumns: FlatSubmissionColumn[] = []
): FlatSubmissionColumn[] => {
  const columns: FlatSubmissionColumn[] = []
  const conditionalsByRow = new Map<number, FlatSubmissionColumn[]>()

  conditionalColumns.forEach((column) => {
    if (column.rowIndex === undefined) return
    const list = conditionalsByRow.get(column.rowIndex) || []
    list.push(column)
    conditionalsByRow.set(column.rowIndex, list)
  })

  for (let rowIndex = 0; rowIndex < meta.rows; rowIndex += 1) {
    for (let colIndex = 0; colIndex < meta.cols; colIndex += 1) {
      const rowHeader = meta.rowHeaders[rowIndex] || `Row ${rowIndex + 1}`
      const colHeader = meta.columnHeaders[colIndex] || `Column ${colIndex + 1}`
      columns.push({
        key: `table-${meta.fieldId}-${rowIndex}-${colIndex}`,
        label: `${meta.label} — ${colHeader} — ${rowHeader}`,
        fieldId: meta.fieldId,
        isTableCell: true,
        tableLabel: meta.label,
        rowIndex,
        colIndex
      })
    }
    // Row-wise conditional edits (file upload, text, etc.) sit directly after that row.
    columns.push(...(conditionalsByRow.get(rowIndex) || []))
  }

  return columns
}

const toConditionalFlatColumn = (column: {
  fieldId: number
  label: string
  parentFieldId: number
  editFieldType?: string
  rowIndex?: number
}): FlatSubmissionColumn => ({
  key: `conditional-${column.fieldId}`,
  label: column.label,
  fieldId: column.fieldId,
  isTableCell: false,
  isConditionalEdit: true,
  parentFieldId: column.parentFieldId,
  editFieldType: column.editFieldType,
  rowIndex: column.rowIndex
})

/** Decode tableFieldId * 100000 + rowIndex * 1000 + editFieldId */
const decodeTableRowConditionalFieldId = (
  fieldId: number,
  tableFieldId: number
): { rowIndex: number; editFieldId: number } | null => {
  const base = tableFieldId * 100000
  if (fieldId < base || fieldId >= base + 100000) return null
  const remainder = fieldId - base
  return {
    rowIndex: Math.floor(remainder / 1000),
    editFieldId: remainder % 1000
  }
}

/**
 * Prefer submission labels/fieldIds over form-definition row placement so
 * "Table — Asset 1 — File upload 1" never appears under Asset 2.
 */
const syncConditionalColumnsWithSubmissionValues = (
  columns: FlatSubmissionColumn[],
  formValueEntries: FormValueEntry[],
  tableMetaByFieldId: Map<number, TableFieldMeta>
): FlatSubmissionColumn[] => {
  const next = columns.map((column) => ({ ...column }))
  const byFieldId = new Map<number, FlatSubmissionColumn>()
  next.forEach((column) => {
    if (column.isConditionalEdit) {
      byFieldId.set(Number(column.fieldId), column)
    }
  })

  formValueEntries.forEach((entry) => {
    const fieldId = Number(entry.fieldId)
    const label = String(entry.label || '').trim()
    if (!fieldId || !label.includes(' — ')) return
    if (parseTableMatrix(entry.value)) return

    const existing = byFieldId.get(fieldId)
    if (existing) {
      // Submission label is the source of truth (includes correct Asset / row header).
      if (label && existing.label !== label) {
        existing.label = label
      }
      tableMetaByFieldId.forEach((_meta, tableFieldId) => {
        const decoded = decodeTableRowConditionalFieldId(fieldId, tableFieldId)
        if (!decoded) return
        if (Number(existing.parentFieldId) === Number(tableFieldId)) {
          existing.rowIndex = decoded.rowIndex
        }
      })
      return
    }

    let parentFieldId: number | undefined
    let rowIndex: number | undefined
    tableMetaByFieldId.forEach((_meta, tableFieldId) => {
      const decoded = decodeTableRowConditionalFieldId(fieldId, tableFieldId)
      if (!decoded) return
      parentFieldId = tableFieldId
      rowIndex = decoded.rowIndex
    })

    if (parentFieldId === undefined) {
      // Non-table conditional: parent is usually the first segment of the label.
      const parentLabel = label.split(' — ')[0]?.trim()
      const parentColumn = next.find(
        (column) => !column.isConditionalEdit && !column.isTableCell && column.label === parentLabel
      )
      parentFieldId = parentColumn?.fieldId
    }

    if (parentFieldId === undefined) return

    const orphan: FlatSubmissionColumn = {
      key: `conditional-${fieldId}`,
      label,
      fieldId,
      isTableCell: false,
      isConditionalEdit: true,
      parentFieldId,
      rowIndex
    }
    byFieldId.set(fieldId, orphan)

    if (rowIndex !== undefined) {
      // Insert after the matching table row cells when possible.
      const insertAfter = next.reduce(
        (lastIndex, column, index) =>
          column.isTableCell &&
          Number(column.fieldId) === Number(parentFieldId) &&
          column.rowIndex === rowIndex
            ? index
            : lastIndex,
        -1
      )
      if (insertAfter >= 0) {
        next.splice(insertAfter + 1, 0, orphan)
        return
      }
    }

    next.push(orphan)
  })

  return next
}

export const extractTableFieldsFromFormValues = (
  formValueEntries: FormValueEntry[]
): Map<number, TableFieldMeta> => {
  const tableFields = new Map<number, TableFieldMeta>()

  formValueEntries.forEach((entry) => {
    const fieldId = Number(entry.fieldId)
    if (!fieldId) return
    const label = String(entry.label || 'Table').trim()
    const merged = mergeTableFieldMeta(tableFields.get(fieldId), entry.value, fieldId, label)
    if (merged) {
      tableFields.set(fieldId, merged)
    }
  })

  return tableFields
}

export const extractTableFieldsMerged = (
  formData: unknown,
  formValueEntries: FormValueEntry[] = []
): Map<number, TableFieldMeta> => {
  const merged = extractTableFieldsFromForm(formData)

  formValueEntries.forEach((entry) => {
    const fieldId = Number(entry.fieldId)
    if (!fieldId) return
    const label = String(entry.label || merged.get(fieldId)?.label || 'Table').trim()
    const nextMeta = mergeTableFieldMeta(merged.get(fieldId), entry.value, fieldId, label)
    if (nextMeta) {
      merged.set(fieldId, nextMeta)
    }
  })

  return merged
}

export const extractTableFieldsForDisplay = (
  formData: unknown,
  formValueEntries: FormValueEntry[] = []
): Map<number, TableFieldMeta> => {
  const merged = extractTableFieldsMerged(formData, formValueEntries)
  const result = new Map<number, TableFieldMeta>()

  merged.forEach((meta, fieldId) => {
    result.set(fieldId, applyDataDrivenTableRows(meta, formValueEntries))
  })

  return result
}

export const buildFlatSubmissionColumns = (
  formData: unknown,
  fallbackFormValue: FormValueEntry[] = []
): FlatSubmissionColumn[] => {
  const normalizedFormData = normalizeFormData(formData)
  const normalizedFallback = normalizeFormValueEntries(fallbackFormValue)
  const tableMetaByFieldId = extractTableFieldsMerged(normalizedFormData, normalizedFallback)
  const formElements = flattenFormPages(normalizedFormData).filter(
    (element) =>
      element?.label &&
      typeof element.label === 'string' &&
      !NON_INPUT_FIELD_TYPES.has(element.type)
  )
  const allConditionalColumns =
    extractConditionalEditColumnsFromForm(normalizedFormData).map(toConditionalFlatColumn)
  const consumedConditionalFieldIds = new Set<number>()
  const columns: FlatSubmissionColumn[] = []
  const expandedTableFieldIds = new Set<number>()

  const takeConditionalsForParent = (
    parentFieldId: number,
    options?: { tableScopedOnly?: boolean; fieldScopedOnly?: boolean }
  ): FlatSubmissionColumn[] => {
    const matched = allConditionalColumns.filter((column) => {
      if (Number(column.parentFieldId) !== Number(parentFieldId)) return false
      if (consumedConditionalFieldIds.has(column.fieldId)) return false
      if (options?.tableScopedOnly && column.rowIndex === undefined) return false
      if (options?.fieldScopedOnly && column.rowIndex !== undefined) return false
      return true
    })
    matched.forEach((column) => consumedConditionalFieldIds.add(column.fieldId))
    return matched
  }

  const appendTableColumns = (fieldId: number, label: string) => {
    if (expandedTableFieldIds.has(fieldId)) return

    const matchingValues = normalizedFallback.filter((entry) => Number(entry.fieldId) === fieldId)
    let meta = tableMetaByFieldId.get(fieldId)

    matchingValues.forEach((entry) => {
      meta = mergeTableFieldMeta(meta, entry.value, fieldId, label) || meta
    })

    if (!meta) return

    meta = applyDataDrivenTableRows(meta, normalizedFallback)
    if (meta.rows === 0) return

    const rowConditionals = takeConditionalsForParent(fieldId, { tableScopedOnly: true })
    columns.push(...buildTableCellColumns(meta, rowConditionals))
    expandedTableFieldIds.add(fieldId)
  }

  if (formElements.length > 0) {
    formElements.forEach((element) => {
      const fieldId = Number(element.fieldId ?? element.id)
      const label = String(element.label).trim()

      if (element.type === 'table') {
        appendTableColumns(fieldId, label)
        return
      }

      columns.push({
        key: `field-${fieldId}-${label}`,
        label,
        fieldId,
        isTableCell: false
      })
      columns.push(...takeConditionalsForParent(fieldId, { fieldScopedOnly: true }))
    })
  } else {
    normalizedFallback.forEach((entry) => {
      const fieldId = Number(entry.fieldId)
      const label = String(entry.label || 'Field').trim()
      const inferredMeta = inferTableMetaFromValue(fieldId, label, entry.value)

      if (inferredMeta) {
        appendTableColumns(fieldId, label)
        return
      }

      columns.push({
        key: `field-${fieldId}-${label}`,
        label,
        fieldId,
        isTableCell: false
      })
      columns.push(...takeConditionalsForParent(fieldId, { fieldScopedOnly: true }))
    })
  }

  tableMetaByFieldId.forEach((meta, fieldId) => {
    appendTableColumns(fieldId, meta.label)
  })

  // Any remaining conditional columns (orphan / unmatched) still appear in the list & excel.
  allConditionalColumns.forEach((column) => {
    if (consumedConditionalFieldIds.has(column.fieldId)) return
    columns.push(column)
  })

  return syncConditionalColumnsWithSubmissionValues(columns, normalizedFallback, tableMetaByFieldId)
}

const findFormValueEntry = (
  formValue: FormValueEntry[],
  column: FlatSubmissionColumn
): FormValueEntry | undefined => {
  if (column.isTableCell) {
    if (column.label) {
      const exactCellEntry = formValue.find((entry) => entry.label === column.label)
      if (exactCellEntry) return exactCellEntry
    }

    const byId = formValue.find((entry) => Number(entry.fieldId) === column.fieldId)
    if (byId && parseTableMatrix(byId.value)) {
      return byId
    }

    if (column.tableLabel) {
      const byTableLabel = formValue.find((entry) => entry.label === column.tableLabel)
      if (byTableLabel && parseTableMatrix(byTableLabel.value)) {
        return byTableLabel
      }
    }

    return undefined
  }

  const byLabel = column.label ? formValue.find((entry) => entry.label === column.label) : undefined
  if (byLabel) return byLabel

  // Prefer main-field entries over conditional composite labels when matching by fieldId.
  const byId = formValue.find(
    (entry) =>
      Number(entry.fieldId) === column.fieldId && !String(entry.label || '').includes(' — ')
  )
  if (byId) return byId

  return formValue.find((entry) => Number(entry.fieldId) === column.fieldId)
}

export const getFlatColumnValue = (
  column: FlatSubmissionColumn,
  formValue: FormValueEntry[],
  tableMetaByFieldId: Map<number, TableFieldMeta>
): string => {
  const entries = normalizeFormValueEntries(formValue)
  const entry = findFormValueEntry(entries, column)
  if (!entry) return ''

  if (!column.isTableCell) {
    return String(entry.value ?? '')
  }

  const meta =
    tableMetaByFieldId.get(column.fieldId) ||
    inferTableMetaFromValue(column.fieldId, column.tableLabel || column.label, entry.value)
  if (!meta || column.rowIndex === undefined || column.colIndex === undefined) {
    return ''
  }

  if (entry.label === column.label && !parseTableMatrix(entry.value)) {
    return String(entry.value ?? '')
  }

  return extractTableCellValue(entry.value, column.rowIndex, column.colIndex, meta)
}

export interface SubmissionDisplayValue {
  displayValue: string
  pendingSubmittedValue?: string
}

const resolveReviewedEditValue = (
  fieldEdit: PrefillFieldEdit,
  storedValue: string,
  originalValue: string,
  submittedValue: string
): string => {
  if (fieldEdit.status === 'approved') {
    return submittedValue || fieldEdit.submittedValue || storedValue
  }
  if (fieldEdit.status === 'rejected') {
    return originalValue || fieldEdit.originalValue || storedValue
  }
  return originalValue || fieldEdit.originalValue || storedValue
}

export const getFieldEditForColumn = (
  edits: PrefillFieldEdit[] | undefined | null,
  column: FlatSubmissionColumn
): PrefillFieldEdit | undefined => {
  const list = edits || []
  if (column.isTableCell) {
    return getPrefillEditForTableCell(list, column.fieldId, column.rowIndex ?? 0)
  }

  const columnLabel = String(column.label || '').trim()

  if (column.isConditionalEdit) {
    const editFieldLabelFromColumn = columnLabel.includes(' — ')
      ? columnLabel.split(' — ').pop()?.trim() || ''
      : columnLabel

    const byFieldId = list.find(
      (edit) => edit.editType === 'conditional' && Number(edit.fieldId) === Number(column.fieldId)
    )
    if (byFieldId) return byFieldId

    const byExactLabel = list.find(
      (edit) => edit.editType === 'conditional' && String(edit.label || '').trim() === columnLabel
    )
    if (byExactLabel) return byExactLabel

    // Table-row conditionals must match rowIndex — never attach Asset 1's edit onto Asset 2.
    if (column.rowIndex !== undefined) {
      return list.find(
        (edit) =>
          edit.editType === 'conditional' &&
          Number(edit.parentFieldId) === Number(column.parentFieldId) &&
          Number(edit.rowIndex) === Number(column.rowIndex) &&
          String(edit.editFieldLabel || '').trim() === editFieldLabelFromColumn
      )
    }

    // Field-scoped conditionals only (no rowIndex on the column).
    return list.find(
      (edit) =>
        edit.editType === 'conditional' &&
        edit.rowIndex === undefined &&
        Number(edit.parentFieldId) === Number(column.parentFieldId) &&
        String(edit.editFieldLabel || '').trim() === editFieldLabelFromColumn
    )
  }

  const mainEdits = list.filter(
    (edit) => edit.editType !== 'conditional' && edit.scope !== 'table_row'
  )

  // Prefer exact label — bare fieldId match attaches Plain Input's edit onto Address
  // when both historically share the same fieldId.
  if (columnLabel) {
    const byLabel = mainEdits.find((edit) => String(edit.label || '').trim() === columnLabel)
    if (byLabel) return byLabel
  }

  const byId = mainEdits.find((edit) => {
    if (Number(edit.fieldId) !== Number(column.fieldId)) return false
    const editLabel = String(edit.label || '').trim()
    if (!editLabel || !columnLabel) return true
    return editLabel === columnLabel
  })
  if (byId) return byId

  // Parent column in conditional-edit mode: no main edit, only child conditionals.
  // Bind by parentLabel so Address never inherits Plain Input's pending main edit.
  if (!columnLabel) return undefined

  const parentConditionals = list.filter(
    (edit) =>
      edit.editType === 'conditional' && String(edit.parentLabel || '').trim() === columnLabel
  )
  if (parentConditionals.length === 0) return undefined

  // Prefer a non-file text value for the list "Proposed" summary.
  const textProposed =
    parentConditionals.find((edit) => {
      const value = String(edit.submittedValue || '').trim()
      if (!value) return false
      if (/^https?:\/\//i.test(value)) return false
      if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|pdf|doc|docx)(\?.*)?$/i.test(value)) return false
      return true
    }) || parentConditionals.find((edit) => String(edit.submittedValue || '').trim())

  const representative = textProposed || parentConditionals[0]
  const parentOriginal =
    String(representative.parentOriginalValue || '').trim() ||
    parentConditionals.map((edit) => String(edit.parentOriginalValue || '').trim()).find(Boolean) ||
    ''

  // Synthetic main-edit shaped object so the list cell can show Current/Proposed
  // without adopting another field's pending main edit via fieldId collision.
  return {
    fieldId: column.fieldId,
    label: columnLabel,
    originalValue: parentOriginal,
    submittedValue: String(representative.submittedValue || '').trim(),
    status: representative.status,
    editType: 'main',
    parentFieldId: representative.parentFieldId,
    parentLabel: representative.parentLabel || columnLabel,
    parentOriginalValue: parentOriginal
  }
}

export const resolveSubmissionDisplayValue = (
  column: FlatSubmissionColumn,
  formValue: FormValueEntry[],
  fieldEdit: PrefillFieldEdit | undefined,
  tableMetaByFieldId: Map<number, TableFieldMeta>
): SubmissionDisplayValue => {
  const storedValue = getFlatColumnValue(column, formValue, tableMetaByFieldId)

  if (column.isConditionalEdit && fieldEdit) {
    if (fieldEdit.status === 'approved') {
      return { displayValue: fieldEdit.submittedValue || storedValue || '' }
    }
    if (fieldEdit.status === 'rejected') {
      return {
        displayValue: fieldEdit.originalValue || storedValue || ''
      }
    }
    // Pending: show prior value (often empty for new file uploads) + proposed.
    const submitted = fieldEdit.submittedValue || storedValue || ''
    return {
      displayValue: fieldEdit.originalValue || storedValue || '',
      pendingSubmittedValue:
        fieldEdit.status === 'pending' && fieldEdit.submittedValue ? submitted : undefined
    }
  }

  if (!fieldEdit) {
    return { displayValue: storedValue }
  }

  if (
    column.isTableCell &&
    fieldEdit.scope === 'table_row' &&
    fieldEdit.rowIndex !== undefined &&
    fieldEdit.rowIndex !== column.rowIndex
  ) {
    return { displayValue: storedValue }
  }

  if (column.isTableCell && column.rowIndex !== undefined && column.colIndex !== undefined) {
    // Conditional row extras (file upload, etc.) use dedicated columns.
    if (fieldEdit.editType === 'conditional') {
      return { displayValue: storedValue }
    }

    if (fieldEdit.scope === 'table_row' && fieldEdit.rowIndex === column.rowIndex) {
      const parseRowValues = (value: string): string[] => {
        try {
          const parsed = JSON.parse(value)
          return Array.isArray(parsed) ? parsed.map((cell) => String(cell ?? '')) : []
        } catch {
          return []
        }
      }

      const originalCells = parseRowValues(fieldEdit.originalValue)
      const submittedCells = parseRowValues(fieldEdit.submittedValue)
      // Only apply when the edit stores a real row array — never overwrite cells with a
      // scalar conditional value (e.g. image URL).
      if (originalCells.length === 0 && submittedCells.length === 0) {
        return { displayValue: storedValue }
      }

      const originalCell = originalCells[column.colIndex] ?? ''
      const submittedCell = submittedCells[column.colIndex] ?? ''
      const displayValue =
        fieldEdit.status === 'approved' ? submittedCell || storedValue : originalCell || storedValue
      const pendingSubmittedValue =
        fieldEdit.status === 'pending' && submittedCell !== displayValue ? submittedCell : undefined

      return { displayValue, pendingSubmittedValue }
    }

    const meta =
      tableMetaByFieldId.get(column.fieldId) ||
      inferTableMetaFromValue(column.fieldId, column.tableLabel || column.label, storedValue)

    if (meta) {
      const originalCell = extractTableCellValue(
        fieldEdit.originalValue,
        column.rowIndex,
        column.colIndex,
        meta
      )
      const submittedCell = extractTableCellValue(
        fieldEdit.submittedValue,
        column.rowIndex,
        column.colIndex,
        meta
      )
      const hasParsedEditValues = Boolean(
        parseTableMatrix(fieldEdit.originalValue) || parseTableMatrix(fieldEdit.submittedValue)
      )

      if (!hasParsedEditValues) {
        return { displayValue: storedValue }
      }

      const displayValue = resolveReviewedEditValue(
        fieldEdit,
        storedValue,
        originalCell,
        submittedCell
      )
      const pendingSubmittedValue =
        fieldEdit.status === 'pending' && submittedCell !== displayValue ? submittedCell : undefined

      return {
        displayValue,
        pendingSubmittedValue
      }
    }

    return { displayValue: storedValue }
  }

  if (fieldEdit.status === 'approved') {
    return {
      displayValue: fieldEdit.submittedValue || storedValue
    }
  }

  if (fieldEdit.status === 'rejected') {
    return {
      displayValue: fieldEdit.originalValue || storedValue
    }
  }

  if (fieldEdit.editType === 'conditional') {
    const contextOriginal = getConditionalEditOriginalDisplay(fieldEdit)
    return {
      displayValue: contextOriginal || storedValue,
      pendingSubmittedValue:
        fieldEdit.status === 'pending' && fieldEdit.submittedValue
          ? fieldEdit.submittedValue
          : undefined
    }
  }

  return {
    displayValue: fieldEdit.originalValue || storedValue,
    pendingSubmittedValue:
      fieldEdit.status === 'pending' && fieldEdit.submittedValue !== fieldEdit.originalValue
        ? fieldEdit.submittedValue
        : undefined
  }
}

export const resolveSubmissionCellEditStatus = (
  column: FlatSubmissionColumn,
  fieldEdit: PrefillFieldEdit | undefined,
  tableMetaByFieldId: Map<number, TableFieldMeta>
): PrefillFieldEdit['status'] | undefined => {
  if (!fieldEdit) return undefined

  if (!column.isTableCell || column.rowIndex === undefined || column.colIndex === undefined) {
    return fieldEdit.status
  }

  if (
    fieldEdit.scope === 'table_row' &&
    fieldEdit.rowIndex !== undefined &&
    fieldEdit.rowIndex !== column.rowIndex
  ) {
    return undefined
  }

  if (fieldEdit.scope === 'table_row' && fieldEdit.rowIndex === column.rowIndex) {
    const parseRowValues = (value: string): string[] => {
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed.map((cell) => String(cell ?? '')) : []
      } catch {
        return []
      }
    }
    const originalCell = parseRowValues(fieldEdit.originalValue)[column.colIndex] ?? ''
    const submittedCell = parseRowValues(fieldEdit.submittedValue)[column.colIndex] ?? ''
    if (originalCell === submittedCell) return undefined
    return fieldEdit.status
  }

  const meta = tableMetaByFieldId.get(column.fieldId)
  if (!meta) return fieldEdit.status

  const originalCell = extractTableCellValue(
    fieldEdit.originalValue,
    column.rowIndex,
    column.colIndex,
    meta
  )
  const submittedCell = extractTableCellValue(
    fieldEdit.submittedValue,
    column.rowIndex,
    column.colIndex,
    meta
  )

  if (originalCell === submittedCell) return undefined
  return fieldEdit.status
}

export const buildSubmissionExportRows = (
  formData: unknown,
  submissions: Array<{
    formValue?: unknown
    recipientName?: string | null
    recipientEmail?: string | null
    updatedAt: string | Date
    prefillFieldEdits?: Array<{
      fieldId: number
      label: string
      originalValue: string
      submittedValue: string
      status: string
      editType?: string
    }> | null
  }>,
  options: {
    includeRecipientColumns: boolean
    includeEditReviewColumns?: boolean
  }
): { headers: string[]; rows: string[][] } => {
  const normalizedFormData = normalizeFormData(formData)
  const sampleFormValue = submissions.flatMap((submission) =>
    normalizeFormValueEntries(submission.formValue)
  )
  const flatColumns = buildFlatSubmissionColumns(normalizedFormData, sampleFormValue)
  const tableMetaByFieldId = extractTableFieldsForDisplay(normalizedFormData, sampleFormValue)

  const headers = [
    ...(options.includeRecipientColumns ? ['Recipient Name', 'Recipient Email'] : []),
    ...flatColumns.map((col) => col.label),
    ...(options.includeEditReviewColumns ? ['Edit Review Status'] : []),
    'Submitted At'
  ]

  const rows = submissions.map((submission) => {
    const formValue = normalizeFormValueEntries(submission.formValue)
    const recipientValues = options.includeRecipientColumns
      ? [String(submission.recipientName || ''), String(submission.recipientEmail || '')]
      : []
    const fieldValues = flatColumns.map((col) => {
      const fieldEdit = getFieldEditForColumn(
        submission.prefillFieldEdits as PrefillFieldEdit[] | undefined,
        col
      )
      const { displayValue } = resolveSubmissionDisplayValue(
        col,
        formValue,
        fieldEdit as PrefillFieldEdit | undefined,
        tableMetaByFieldId
      )
      return displayValue
    })
    const editReviewStatus = options.includeEditReviewColumns
      ? [
          (() => {
            const edits = submission.prefillFieldEdits || []
            if (edits.length === 0) return 'No edits'
            const pending = edits.filter((edit) => edit.status === 'pending').length
            const approved = edits.filter((edit) => edit.status === 'approved').length
            const rejected = edits.filter((edit) => edit.status === 'rejected').length
            const parts: string[] = []
            if (pending > 0) parts.push(`${pending} pending`)
            if (approved > 0) parts.push(`${approved} approved`)
            if (rejected > 0) parts.push(`${rejected} rejected`)
            return parts.join(', ')
          })()
        ]
      : []
    const updatedAt = new Date(submission.updatedAt).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    return [...recipientValues, ...fieldValues, ...editReviewStatus, updatedAt]
  })

  return { headers, rows }
}

export const escapeCsvValue = (value: string): string =>
  `"${String(value ?? '').replace(/"/g, '""')}"`
